import { Badge } from "primereact/badge";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import { ProgressSpinner } from "primereact/progressspinner";
import { TabPanel, TabView } from "primereact/tabview";
import React, { useCallback, useEffect, useState } from "react";
import * as Icon from "react-bootstrap-icons";
import { useSelector } from "react-redux";
import { Booking } from "../models/booking.model";
import { bookingService } from "../service/bookings.service";
import PageHeader from "./PageHeader";
import "./Reservation.css";

const Reservation: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [b2bBookings, setB2bBookings] = useState<Booking[]>([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [b2bLoading, setB2bLoading] = useState(false);
  const [globalFilter, setGlobalFilter] = useState("");
  const [expandedRows, setExpandedRows] = useState<any>(null);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [dateRangeFilter, setDateRangeFilter] = useState<Date[] | null>(null);

  // Status edit dialog states
  const [showStatusDialog, setShowStatusDialog] = useState(false);

  const [selectedBookingRoom, setSelectedBookingRoom] = useState<{
    bookingId: number;
    roomId: number;
    currentStatus: string;
    userId: number;
  } | null>(null);
  const [newStatus, setNewStatus] = useState("");

  // Get user role from Redux store
  const userRole = useSelector((state: any) => state.auth.userRole) ?? "";
  const loggedInUserId = useSelector((state: any) => state.auth.userId) ?? 0;

  // Debug: Log user role
  console.log("Current user role in Reservation:", userRole);
  console.log(
    "Is admin role?",
    userRole === "RMCAdmin" ||
      userRole === "Admin" ||
      userRole === "SuperAdministrator"
  );

  // Status options for admin
  const statusOptions = [
    { label: "Reserved", value: "Reserved" },
    { label: "Confirmed", value: "Confirmed" },
    { label: "Closed", value: "Closed" },
    { label: "Cancelled", value: "Cancelled" },
  ];

  // Enhanced formatting with default times for check-in/check-out
  const formatCheckInDateTime = useCallback((dateString: string) => {
    if (!dateString) return "Date not available";
    try {
      console.log("Formatting check-in date:", dateString); // Debug log
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "Invalid date";

      const dateOptions: Intl.DateTimeFormatOptions = {
        day: "2-digit",
        month: "short",
        year: "numeric",
      };

      const datePart = date.toLocaleDateString("en-IN", dateOptions);

      // Check if time is midnight, if so, use default check-in time
      const hours = date.getHours();
      const minutes = date.getMinutes();

      console.log("Check-in time - Hours:", hours, "Minutes:", minutes); // Debug log

      if (hours === 0 && minutes === 0) {
        return `${datePart}, 2:00 PM`; // Default check-in time
      } else {
        const timeOptions: Intl.DateTimeFormatOptions = {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        };
        const timePart = date.toLocaleTimeString("en-IN", timeOptions);
        return `${datePart}, ${timePart}`;
      }
    } catch (error) {
      console.error("Error formatting check-in date:", dateString, error);
      return "Date format error";
    }
  }, []);

  const formatBookedDateTime = useCallback((dateString: string) => {
    if (!dateString) return "Date not available";
    try {
      console.log("Formatting booked date:", dateString); // Debug log
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "Invalid date";

      const dateOptions: Intl.DateTimeFormatOptions = {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      };

      return date.toLocaleString("en-IN", dateOptions);
    } catch (error) {
      console.error("Error formatting booked date:", dateString, error);
      return "Date format error";
    }
  }, []);

  const formatCheckOutDateTime = useCallback((dateString: string) => {
    if (!dateString) return "Date not available";
    try {
      console.log("Formatting check-out date:", dateString); // Debug log
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "Invalid date";

      const dateOptions: Intl.DateTimeFormatOptions = {
        day: "2-digit",
        month: "short",
        year: "numeric",
      };

      const datePart = date.toLocaleDateString("en-IN", dateOptions);

      // Check if time is midnight, if so, use default check-out time
      const hours = date.getHours();
      const minutes = date.getMinutes();

      console.log("Check-out time - Hours:", hours, "Minutes:", minutes); // Debug log

      if (hours === 0 && minutes === 0) {
        return `${datePart}, 11:00 AM`; // Default check-out time
      } else {
        const timeOptions: Intl.DateTimeFormatOptions = {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        };
        const timePart = date.toLocaleTimeString("en-IN", timeOptions);
        return `${datePart}, ${timePart}`;
      }
    } catch (error) {
      console.error("Error formatting check-out date:", dateString, error);
      return "Date format error";
    }
  }, []);

  // Flatten bookings for better table display
  const flattenBookingsForTable = useCallback((groupedData: any[]) => {
    const flattened: any[] = [];
    let globalIndex = 0;
    groupedData.forEach((group) => {
      group.bookings.forEach((booking: any, index: number) => {
        globalIndex++;
        flattened.push({
          ...booking,
          groupName: group.groupName,
          bookingIndex: index + 1,
          uniqueRowId: `${booking.id || globalIndex}-${group.groupName}`, // Create unique identifier
          totalRooms: booking.rooms?.length || 0,
          primaryStatus: booking.rooms?.[0]?.status || "Unknown",
          checkInDate: new Date(booking.rawCheckIn || booking.checkIn),
          checkOutDate: new Date(booking.rawCheckOut || booking.checkOut),
        });
      });
    });
    return flattened;
  }, []);

  // Column templates for DataTable
  const groupNameTemplate = (rowData: any) => (
    <div className="flex align-items-center">
      <i className="pi pi-building mr-2 text-primary"></i>
      <span className="font-semibold">{rowData.groupName}</span>
    </div>
  );

  const userTemplate = (rowData: any) => (
    <div>
      <div className="font-semibold">{rowData.fullName}</div>
      <div className="text-sm text-gray-600">{rowData.mobileNumber}</div>
      <div className="text-sm text-gray-600">{rowData.emailAddress}</div>
    </div>
  );

  const statusTemplate = (rowData: any) => {
    const getSeverity = (status: string) => {
      switch (status?.toLowerCase()) {
        case "confirmed":
          return "success";
        case "reserved":
          return "info";
        case "cancelled":
          return "danger";
        case "pending":
          return "warning";
        default:
          return "secondary";
      }
    };

    // New behavior: if any room in the booking is Confirmed, show Confirmed only (no room counts)
    const anyRoomConfirmed =
      Array.isArray(rowData.rooms) &&
      rowData.rooms.some(
        (r: any) => String(r.status).toLowerCase() === "confirmed"
      );

    if (anyRoomConfirmed) {
      return (
        <div>
          <Badge value={"Confirmed"} severity={getSeverity("Confirmed")} />
        </div>
      );
    }

    return (
      <div>
        <Badge
          value={rowData.primaryStatus}
          severity={getSeverity(rowData.primaryStatus)}
        />
        <div className="text-sm mt-1">
          {rowData.totalRooms} room{rowData.totalRooms !== 1 ? "s" : ""}
        </div>
      </div>
    );
  };

  const datesTemplate = (rowData: any) => (
    <div>
      <div>
        <strong>In:</strong> {rowData.checkIn}
      </div>
      <div>
        <strong>Out:</strong> {rowData.checkOut}
      </div>
    </div>
  );

  // Status edit handlers
  const openStatusDialog = (rowData: any) => {
    // For now, use the first room's details - this could be enhanced to show a room selection dialog
    const firstRoom = rowData.rooms?.[0];
    console.log(
      "Opening status dialog for booking:",
      firstRoom.roomId,
      "with current status:",
      firstRoom.status
    );
    if (firstRoom) {
      setSelectedBookingRoom({
        bookingId: rowData.id,
        roomId: firstRoom.roomId,
        currentStatus: firstRoom.status,
        userId: rowData.userId, // Get userId from the booking data
      });
      setNewStatus(firstRoom.status);
      setShowStatusDialog(true);
    }
  };

  const handleStatusUpdate = async () => {
    if (!selectedBookingRoom) {
      console.log("No selectedBookingRoom found");
      return;
    }

    // Use userId from the booking data (the user who made the reservation)
    const bookingUserId = selectedBookingRoom.userId;
    if (!bookingUserId) {
      console.error("No userId found in booking data");
      return;
    }

    console.log("Updating status for:", {
      bookingId: selectedBookingRoom.bookingId,
      roomId: selectedBookingRoom.roomId,
      currentStatus: selectedBookingRoom.currentStatus,
      newStatus: newStatus,
      bookingUserId: bookingUserId,
      loggedInUserId: loggedInUserId,
    });

    try {
      let success = false;

      // If changing to "Confirmed", use the special confirmation API
      if (newStatus === "Confirmed") {
        console.log("Confirming room reservation - will cancel other rooms");
        success = await bookingService.confirmRoomReservation(
          selectedBookingRoom.roomId,
          bookingUserId.toString()
        );
      } else {
        // For other status changes, use the standard update API
        success = await bookingService.updateBookingStatus(
          selectedBookingRoom.bookingId,
          selectedBookingRoom.roomId,
          newStatus
        );
      }

      console.log("Update success:", success);

      if (success) {
        console.log("Refreshing bookings data...");
        // Refresh bookings data
        if (activeIndex === 0) {
          await fetchB2bBookings();
        } else {
          await fetchBookings();
        }
        setShowStatusDialog(false);
        setSelectedBookingRoom(null);
        console.log("Status update completed successfully");
      } else {
        console.error("Failed to update status - success was false");
      }
    } catch (error) {
      console.error("Error during status update:", error);
    }
  };

  const actionsTemplate = (rowData: any) => {
    console.log("ActionsTemplate called with userRole:", userRole);
    console.log(
      "Should show edit button:",
      userRole === "RMCAdmin" ||
        userRole === "Admin" ||
        userRole === "SuperAdministrator"
    );

    return (
      <div className="flex gap-2">
        <Button
          icon="pi pi-eye"
          className="p-button-rounded p-button-text p-button-sm"
          tooltip="View Details"
        />
        {(userRole === "RMCAdmin" ||
          userRole === "Admin" ||
          userRole === "SuperAdministrator") && (
          <Button
            icon="pi pi-pencil"
            className="p-button-rounded p-button-text p-button-sm p-button-warning"
            tooltip="Edit Reservation Status"
            onClick={() => openStatusDialog(rowData)}
          />
        )}
      </div>
    );
  };

  const rowExpansionTemplate = (data: any) => {
    return (
      <div className="reservation-expanded-content-new">
        {/* Header Section */}
        <div className="expansion-header">
          <div className="flex justify-content-between align-items-center">
            <h4 className="expansion-title">
              <i className="pi pi-building mr-2"></i>
              Booking Details for {data.fullName}
            </h4>
          </div>

          {/* Booked Date Information */}
          <div className="booking-meta-info">
            <span className="booked-date">
              <i className="pi pi-calendar mr-2"></i>
              <strong>Booked on:</strong> {data.bookedOn}
            </span>
          </div>
        </div>

        {/* Booking Timeline Section */}
        <div className="booking-timeline">
          <div className="timeline-container">
            <div className="timeline-item checkin">
              <div className="timeline-icon checkin-icon">
                <i className="pi pi-sign-in"></i>
              </div>
              <div className="timeline-content">
                <div className="timeline-title">Check-in</div>
                <div className="timeline-date">
                  {data.checkIn || "Check-in date not available"}
                </div>
              </div>
            </div>

            <div className="timeline-connector">
              <div className="timeline-line"></div>
              <div className="timeline-duration">
                <i className="pi pi-clock mr-1"></i>
                {(() => {
                  const checkInDate = data.rawCheckIn || data.checkIn;
                  const checkOutDate = data.rawCheckOut || data.checkOut;
                  if (!checkInDate || !checkOutDate) {
                    return "Duration not available";
                  }
                  try {
                    const checkIn = new Date(checkInDate);
                    const checkOut = new Date(checkOutDate);
                    if (isNaN(checkIn.getTime()) || isNaN(checkOut.getTime())) {
                      return "Invalid dates";
                    }
                    const diffTime = Math.abs(
                      checkOut.getTime() - checkIn.getTime()
                    );
                    const diffDays = Math.ceil(
                      diffTime / (1000 * 60 * 60 * 24)
                    );
                    return `${diffDays} night${diffDays !== 1 ? "s" : ""}`;
                  } catch (error) {
                    return "Error calculating duration";
                  }
                })()}
              </div>
            </div>

            <div className="timeline-item checkout">
              <div className="timeline-icon checkout-icon">
                <i className="pi pi-sign-out"></i>
              </div>
              <div className="timeline-content">
                <div className="timeline-title">Check-out</div>
                <div className="timeline-date">
                  {data.checkOut || "Check-out date not available"}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Guest Information Panel */}
        <div className="guest-info-panel">
          <div className="guest-contact-info">
            <h5 className="guest-info-title">
              <i className="pi pi-user-edit mr-2"></i>
              Guest Information
            </h5>
            <div className="contact-grid">
              <div className="contact-item">
                <i className="pi pi-phone text-primary"></i>
                <span>
                  <strong>Phone:</strong> {data.mobileNumber}
                </span>
              </div>
              <div className="contact-item">
                <i className="pi pi-envelope text-primary"></i>
                <span>
                  <strong>Email:</strong> {data.emailAddress}
                </span>
              </div>
              {data.clientNames && (
                <div className="contact-item full-width">
                  <i className="pi pi-users text-primary"></i>
                  <span>
                    <strong>Clients:</strong> {data.clientNames}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="guest-summary-stats">
            <div className="stat-card adults">
              <div className="stat-icon">
                <i className="pi pi-user"></i>
              </div>
              <div className="stat-content">
                <div className="stat-number">
                  {data.rooms?.[0]?.adults || 0}
                </div>
                <div className="stat-label">Adults</div>
              </div>
            </div>

            <div className="stat-card children">
              <div className="stat-icon">
                <i className="pi pi-users"></i>
              </div>
              <div className="stat-content">
                <div className="stat-number">
                  {data.rooms?.[0]?.children || 0}
                </div>
                <div className="stat-label">Children</div>
              </div>
            </div>

            <div className="stat-card rooms">
              <div className="stat-icon">
                <i className="pi pi-home"></i>
              </div>
              <div className="stat-content">
                <div className="stat-number">{data.rooms?.length || 0}</div>
                <div className="stat-label">Rooms</div>
              </div>
            </div>
          </div>
        </div>

        {/* Property Information Bar */}
        <div className="property-info-bar">
          <div className="flex flex-wrap gap-4">
            <div className="info-item">
              <i className="pi pi-map-marker text-primary"></i>
              <span>
                <strong>Property:</strong> {data.property}
              </span>
            </div>
            <div className="info-item">
              <i className="pi pi-compass text-primary"></i>
              <span>
                <strong>Zone:</strong> {data.zone}
              </span>
            </div>
            <div className="info-item">
              <i className="pi pi-home text-primary"></i>
              <span>
                <strong>Address:</strong> {data.address}
              </span>
            </div>
          </div>
        </div>

        {/* Rooms Section - Full Width Table Style */}
        <div className="rooms-section">
          <h5 className="rooms-title">
            <i className="pi pi-th-large mr-2"></i>
            Room Assignments
          </h5>

          <div className="rooms-table">
            <div className="rooms-table-header">
              <div className="room-col-image">Image</div>
              <div className="room-col-name">Room Name</div>
              <div className="room-col-status">Status</div>
              <div className="room-col-guests">Guests</div>
              <div className="room-col-priority">Priority</div>
              <div className="room-col-actions">Actions</div>
            </div>

            {data.rooms?.map((room: any, index: number) => {
              const roomImages = room.roomImages || [];
              const mainImage =
                roomImages.find((img: any) => img.isMainImage) || roomImages[0];

              return (
                <div key={index} className="rooms-table-row">
                  <div className="room-col-image">
                    {mainImage ? (
                      <img
                        src={mainImage.imageUrl}
                        alt={room.name}
                        className="room-table-thumbnail"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    ) : (
                      <div className="room-table-thumbnail-placeholder">
                        <i className="pi pi-image"></i>
                      </div>
                    )}
                  </div>

                  <div className="room-col-name">
                    <div className="room-name-primary">{room.name}</div>
                    <div className="room-name-secondary">Room #{index + 1}</div>
                  </div>

                  <div className="room-col-status">
                    <Badge
                      value={room.status}
                      severity={
                        statusTemplate({ primaryStatus: room.status }).props
                          .severity
                      }
                      className="status-badge"
                    />
                  </div>

                  <div className="room-col-guests">
                    <div className="guest-info">
                      <div className="guest-count">
                        <i className="pi pi-user mr-1"></i>
                        {room.adults || 0} Adult
                        {(room.adults || 0) !== 1 ? "s" : ""}
                      </div>
                      {room.children > 0 && (
                        <div className="guest-count children">
                          <i className="pi pi-users mr-1"></i>
                          {room.children} Child
                          {room.children !== 1 ? "ren" : ""}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="room-col-priority">
                    {room.priority > 0 ? (
                      <span className="priority-badge">
                        <i className="pi pi-star-fill mr-1"></i>
                        Priority {room.priority}
                      </span>
                    ) : (
                      <span className="no-priority">Standard</span>
                    )}
                  </div>

                  <div className="room-col-actions">
                    {/* <Button
                      icon="pi pi-eye"
                      className="p-button-text p-button-sm"
                      tooltip="View Details"
                      onClick={() => {
                        if (mainImage) {
                          window.open(mainImage.imageUrl, "_blank");
                        }
                      }}
                    /> */}
                    <Button
                      icon="pi pi-pencil"
                      className="p-button-text p-button-sm ml-1"
                      tooltip="Edit Room Status"
                      onClick={() => {
                        setSelectedBookingRoom({
                          bookingId: data.id,
                          roomId: room.roomId,
                          currentStatus: room.status,
                          userId: data.userId, // Get userId from the booking data
                        });
                        setNewStatus(room.status);
                        setShowStatusDialog(true);
                      }}
                    />
                  </div>
                </div>
              );
            }) || (
              <div className="rooms-table-empty">
                <i className="pi pi-info-circle mr-2"></i>
                No room details available for this booking
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const fetchBookings = useCallback(async () => {
    setBookingsLoading(true);
    try {
      const result = await bookingService.getAll();
      console.log("B2C Bookings raw result:", result);

      // Log sample booking to see date structure
      if (result && result.length > 0) {
        console.log("Sample B2C booking:", result[0]);
        console.log("B2C checkIn:", result[0].checkIn);
        console.log("B2C checkOut:", result[0].checkOut);
      }

      setBookings(result);
    } catch {
      setBookings([]);
    } finally {
      setBookingsLoading(false);
    }
  }, []);

  const fetchB2bBookings = useCallback(async () => {
    setB2bLoading(true);
    try {
      const result = await bookingService.getAllB2BUserBookings();
      console.log("B2B Bookings raw result:", result);

      // Log sample booking to see date structure
      if (result && result.length > 0) {
        console.log("Sample B2B booking:", result[0]);
        console.log("B2B checkIn:", result[0].checkIn);
        console.log("Booked Rooms:", result[0].bookedRooms);
      }

      setB2bBookings(result);
    } catch {
      setB2bBookings([]);
    } finally {
      setB2bLoading(false);
    }
  }, []);

  const handleTabChange = async (e: { index: number }) => {
    setActiveIndex(e.index);
    if (e.index === 0 && b2bBookings.length === 0) {
      await fetchB2bBookings();
    } else if (e.index === 1 && bookings.length === 0) {
      await fetchBookings();
    }
  };

  const groupBookingsByRMC = useCallback(
    (list: Booking[], isB2B: boolean = false) => {
      if (!isB2B) {
        // For B2C, group by property since no RMC
        const grouped = list.reduce((acc, booking) => {
          const propertyName = booking.property?.name || "Unknown Property";
          if (!acc[propertyName]) {
            acc[propertyName] = [];
          }
          acc[propertyName].push(booking);
          return acc;
        }, {} as Record<string, Booking[]>);
        return Object.entries(grouped).map(([propertyName, bookings]) => ({
          groupName: propertyName,
          bookings: bookings.map((booking, idx) => ({
            id: booking.id,
            fullName: `${booking.firstName} ${booking.lastName}`,
            mobileNumber: booking.mobileNumber,
            emailAddress: booking.emailAddress,
            address: booking.property?.address || "",
            zone: booking.property?.location?.zone?.name || "",
            property: booking.property?.name || "",
            checkIn: formatCheckInDateTime(booking.checkIn),
            checkOut: formatCheckOutDateTime(booking.checkOut),
            bookedOn: booking.createdDate
              ? formatBookedDateTime(booking.createdDate)
              : "N/A",
            rawCheckIn: booking.checkIn, // Keep raw date for calculations
            rawCheckOut: booking.checkOut, // Keep raw date for calculations
            rooms:
              booking.bookedRooms?.map((br) => {
                // Handle both array and $values format for room images
                const roomImages =
                  br.room && Array.isArray(br.room.roomImages)
                    ? br.room.roomImages
                    : (br.room?.roomImages as any)?.$values || [];
                return {
                  roomId: br.room?.id, // <-- Fix: add roomId
                  name: br.room?.name || "Unknown Room",
                  status: br.status || "Unknown",
                  adults: booking.noOfAdults || 0,
                  children: booking.noOfChildrens || 0,
                  priority: br.priority || 0,
                  roomImages: roomImages,
                };
              }) || [],
          })),
        }));
      }

      // For B2B, group by RMC
      const grouped = list.reduce((acc, booking) => {
        const rmcName = booking.rmc?.name || "Unknown RMC";
        if (!acc[rmcName]) {
          acc[rmcName] = [];
        }
        acc[rmcName].push(booking);
        return acc;
      }, {} as Record<string, Booking[]>);

      return Object.entries(grouped).map(([rmcName, bookings]) => ({
        groupName: rmcName,
        bookings: bookings.map((booking) => ({
          id: booking.id, // <-- Use real bookingId
          userId: booking.userId, // Keep userId from booking
          fullName: `${booking.firstName} ${booking.lastName}`,
          mobileNumber: booking.mobileNumber,
          emailAddress: booking.emailAddress,
          address: booking.property?.address || "",
          zone: booking.property?.location?.zone?.name || "",
          property: booking.property?.name || "",
          checkIn: formatCheckInDateTime(booking.checkIn),
          checkOut: formatCheckOutDateTime(booking.checkOut),
          bookedOn: booking.createdDate
            ? formatBookedDateTime(booking.createdDate)
            : "N/A",
          clientNames: booking.clients?.map((c) => c.name).join(", ") || "",
          rooms:
            booking.bookedRooms?.map((br) => {
              // Handle both array and $values format for room images
              const roomImages =
                br.room && Array.isArray(br.room.roomImages)
                  ? br.room.roomImages
                  : (br.room?.roomImages as any)?.$values || [];
              return {
                roomId: br.room?.id,
                name: br.room?.name || "Unknown Room",
                status: br.status || "Unknown",
                adults: booking.noOfAdults || 0,
                children: booking.noOfChildrens || 0,
                priority: br.priority || 0,
                roomImages: roomImages,
              };
            }) || [],
        })),
      }));
    },
    [formatCheckInDateTime, formatCheckOutDateTime, formatBookedDateTime]
  );

  const renderEnhancedBookingTable = (
    groupedData: any[],
    loading: boolean,
    isB2B: boolean = false
  ) => {
    if (loading) {
      return (
        <div className="text-center my-5">
          <ProgressSpinner />
        </div>
      );
    }

    if (!groupedData.length) {
      return (
        <div className="text-center my-5">
          <p>No reservations found.</p>
        </div>
      );
    }

    const flattenedData = flattenBookingsForTable(groupedData);

    // Status options for filtering
    const statusOptions = [
      { label: "All Status", value: "" },
      { label: "Confirmed", value: "Confirmed" },
      { label: "Reserved", value: "Reserved" },
      { label: "Pending", value: "Pending" },
      { label: "Cancelled", value: "Cancelled" },
    ];

    // Apply filters
    const filteredData = flattenedData.filter((booking) => {
      const matchesGlobalFilter =
        !globalFilter ||
        Object.values(booking).some((value: any) =>
          value?.toString().toLowerCase().includes(globalFilter.toLowerCase())
        );

      const matchesStatusFilter =
        !statusFilter || booking.primaryStatus === statusFilter;

      const matchesDateFilter =
        !dateRangeFilter ||
        !dateRangeFilter[0] ||
        !dateRangeFilter[1] ||
        (booking.checkInDate >= dateRangeFilter[0] &&
          booking.checkInDate <= dateRangeFilter[1]);

      return matchesGlobalFilter && matchesStatusFilter && matchesDateFilter;
    });

    const header = (
      <div className="reservation-header-container">
        <div className="reservation-filters-grid">
          <IconField iconPosition="left" className="filter-search">
            <InputIcon>
              <Icon.Search className="text-sm" />
            </InputIcon>
            <InputText
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              placeholder="Search all fields..."
              className="p-inputtext-sm"
            />
          </IconField>

          <Dropdown
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.value)}
            options={statusOptions}
            placeholder="Filter by Status"
            className="filter-status p-inputtext-sm"
            appendTo="self"
          />

          <Calendar
            value={dateRangeFilter}
            onChange={(e) => setDateRangeFilter(e.value as Date[] | null)}
            selectionMode="range"
            readOnlyInput
            hideOnRangeSelection
            placeholder="Select date range..."
            className="filter-date p-inputtext-sm"
          />
        </div>

        <div className="reservation-actions-row">
          <Button
            type="button"
            icon="pi pi-filter-slash"
            label="Clear Filters"
            className="p-button-outlined p-button-secondary p-button-sm"
            onClick={() => {
              setGlobalFilter("");
              setStatusFilter("");
              setDateRangeFilter(null);
            }}
          />
          {/* <Button
            type="button"
            icon="pi pi-download"
            label="Export"
            className="p-button-outlined p-button-sm"
            onClick={() => {
              // Export functionality can be implemented here
              console.log("Export clicked for:", filteredData);
            }}
          /> */}
        </div>
      </div>
    );

    return (
      <div className="booking-table-container">
        <DataTable
          value={filteredData}
          header={header}
          expandedRows={expandedRows}
          onRowToggle={(e) => setExpandedRows(e.data)}
          rowExpansionTemplate={rowExpansionTemplate}
          dataKey="uniqueRowId"
          paginator
          rows={15}
          rowsPerPageOptions={[10, 15, 25, 50]}
          sortMode="multiple"
          removableSort
          className="p-datatable-gridlines reservation-datatable"
          emptyMessage={`No ${
            isB2B ? "B2B" : "B2C"
          } reservations found with current filters.`}
          size="small"
        >
          <Column expander style={{ width: "3rem" }} />

          <Column
            field="groupName"
            header={isB2B ? "RMC" : "Property"}
            body={groupNameTemplate}
            sortable
            // filter
            style={{ minWidth: "200px" }}
          />

          <Column
            field="fullName"
            header="Guest Information"
            body={userTemplate}
            sortable
            style={{ minWidth: "250px" }}
          />

          <Column
            field="primaryStatus"
            header="Status & Rooms"
            body={statusTemplate}
            sortable
            style={{ minWidth: "150px" }}
          />

          <Column
            field="checkInDate"
            header="Check-in / Check-out"
            body={datesTemplate}
            sortable
            style={{ minWidth: "200px" }}
          />

          {isB2B && (
            <Column
              field="clientNames"
              header="Client(s)"
              sortable
              style={{ minWidth: "180px" }}
            />
          )}

          {/* <Column
            header="Actions"
            body={actionsTemplate}
            style={{ minWidth: '100px' }}
          /> */}
        </DataTable>
      </div>
    );
  };

  useEffect(() => {
    fetchB2bBookings(); // load default tab data
  }, [fetchB2bBookings]);

  return (
    <div className="content reservation-container">
      <PageHeader title="Reservation List" showBackButton={false} />
      <div className="content-body">
        <div className="container mt-4">
          <div className="row shadow">
            <div className="col-md-12 mb-2">
              <TabView
                activeIndex={activeIndex}
                onTabChange={handleTabChange}
                className="reservation-tabs"
              >
                <TabPanel header="B2B Reservations (By RMC)">
                  {renderEnhancedBookingTable(
                    groupBookingsByRMC(b2bBookings, true),
                    b2bLoading,
                    true
                  )}
                </TabPanel>
                <TabPanel header="B2C Reservations (By Property)">
                  {renderEnhancedBookingTable(
                    groupBookingsByRMC(bookings, false),
                    bookingsLoading,
                    false
                  )}
                </TabPanel>
              </TabView>
            </div>
          </div>
        </div>
      </div>

      {/* Status Edit Dialog */}
      <Dialog
        header="Update Reservation Status"
        visible={showStatusDialog}
        style={{ width: "500px" }}
        onHide={() => setShowStatusDialog(false)}
        footer={
          <div className="flex justify-content-end gap-2">
            <Button
              label="Cancel"
              icon="pi pi-times"
              className="p-button-text"
              onClick={() => setShowStatusDialog(false)}
            />
            <Button
              label="Update"
              icon="pi pi-check"
              onClick={handleStatusUpdate}
              disabled={
                !newStatus || newStatus === selectedBookingRoom?.currentStatus
              }
            />
          </div>
        }
      >
        {selectedBookingRoom && (
          <div className="field">
            <label htmlFor="status" className="block">
              Current Status:{" "}
              <strong>{selectedBookingRoom.currentStatus}</strong>
            </label>
            <label htmlFor="status" className="block mt-3 mb-2">
              New Status:
            </label>
            <Dropdown
              id="status"
              value={newStatus}
              onChange={(e) => setNewStatus(e.value)}
              options={statusOptions}
              placeholder="Select new status"
              className="w-full"
              appendTo="self"
            />

            {newStatus === "Confirmed" &&
              newStatus !== selectedBookingRoom.currentStatus && (
                <div
                  className="mt-3 p-3 border-round"
                  style={{
                    backgroundColor: "#fff3cd",
                    border: "1px solid #ffc107",
                  }}
                >
                  <div className="flex align-items-start">
                    <i
                      className="pi pi-exclamation-triangle mr-2"
                      style={{ color: "#856404", fontSize: "1.2rem" }}
                    ></i>
                    <div style={{ color: "#856404" }}>
                      <strong>Warning:</strong> Confirming this room will
                      automatically:
                      <ul className="mt-2 mb-0 pl-3">
                        <li>
                          Mark this room as <strong>Confirmed</strong>
                        </li>
                        <li>Cancel all other reserved rooms in this booking</li>
                        <li>Update your shortlist accordingly</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default Reservation;
